import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { browserLang, interpolate, langFromUrl, readStoredLang, writeStoredLang, type Lang } from './lang';
import { formatDate, formatDateTime, formatMoney, formatTime } from './format';
import { en, type MessageKey } from './messages/en';
import { es } from './messages/es';

// SAM-683 — EN/ES for the signed-in admin. Same mechanism as the public pages (./lang.ts);
// the admin keeps its own preference and defaults to the browser's language:
//   ?lang= → localStorage `samAdminLang` → navigator.languages → English.
// Only rendered text goes through t(): status codes, enum values and error_code keys stay
// raw in state and in requests, and are mapped to a key at render time.

export const ADMIN_LANG_STORAGE_KEY = 'samAdminLang';

const MESSAGES: Record<Lang, Record<MessageKey, string>> = { en, es };

export type TFunction = (key: MessageKey, vars?: Record<string, string | number>) => string;

export interface AdminI18n {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
  /** Display-only formatting in the active language (see ./format.ts). */
  fmt: {
    money: (amount: number | string | null | undefined, currency: string | null | undefined) => string;
    date: (value: string | number | Date | null | undefined) => string;
    dateTime: (value: string | number | Date | null | undefined) => string;
    time: (value: string | number | Date | null | undefined) => string;
  };
}

export const readAdminLang = (): Lang => langFromUrl() ?? readStoredLang(ADMIN_LANG_STORAGE_KEY) ?? browserLang('en');

export const translate = (lang: Lang, key: MessageKey, vars?: Record<string, string | number>): string =>
  interpolate(MESSAGES[lang]?.[key] ?? en[key] ?? String(key), vars);

const build = (lang: Lang, setLang: (l: Lang) => void): AdminI18n => ({
  lang,
  setLang,
  t: (key, vars) => translate(lang, key, vars),
  fmt: {
    money: (amount, currency) => formatMoney(amount, currency, lang),
    date: (value) => formatDate(value, lang),
    dateTime: (value) => formatDateTime(value, lang),
    time: (value) => formatTime(value, lang),
  },
});

const Ctx = createContext<AdminI18n | null>(null);

export const AdminLangProvider: React.FC<{ children: React.ReactNode; initialLang?: Lang }> = ({ children, initialLang }) => {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? readAdminLang());

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    writeStoredLang(ADMIN_LANG_STORAGE_KEY, l);
  }, []);

  const value = useMemo(() => build(lang, setLang), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/**
 * Translations for admin screens. Works without a provider (unit tests, a component rendered
 * outside the tree): it then reads the stored preference and cannot switch.
 */
export const useT = (): AdminI18n => {
  const ctx = useContext(Ctx);
  return ctx ?? build(readAdminLang(), () => {});
};

export type { Lang, MessageKey };
